/* eslint-disable react-hooks/exhaustive-deps */
import { AddPlusIcon } from "icons";
import { useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";
import { Button, Empty, Spinner, Text } from "ui-components";

import {
  useMessageGetConversation,
  useMessageGetConversations,
  useUserSearchConnections,
} from "@/src/hooks/api/generated";
import { useAuth } from "@/src/hooks/useAuth";
import { useChat } from "@/src/hooks/useChat";
import { useChatFunctions } from "@/src/hooks/useChatFunctions";
import { useNavigate } from "@/src/hooks/useNavigate";

import { ConservationItem } from "./ConservationItem";
import { ConversationSearchField } from "./ConversationSearchField";
import { NewConversationCard } from "./NewConversationCard";

export function ConversationList(): JSX.Element {
  const [conversationSearchTerm, setConversationSearchTerm] =
    useState<string>("");
  const { mutate: searchConnectionForChat } = useUserSearchConnections();

  const searchParams = useSearchParams();
  const { authData } = useAuth();
  const recipientIdQueryParam = searchParams.get("recipient");

  const {
    chatMetaData,
    setChatMetaData,
    messages,
    setMessages,
    conversations,
    setConversations,
    selectedConversation,
  } = useChat();

  const {
    changeSearchConnectionStatus,
    setSelectedConversationByConversationData,
    setSelectedConversationByUserData,
    getUserMessages,
    getOtherConversationUser,
  } = useChatFunctions();
  const { pushQueryParam } = useNavigate();

  const { mutate: getConversation } = useMessageGetConversation();

  const { data: conversationsData, isLoading } = useMessageGetConversations(
    {
      page: "1",
      pageSize: "100",
      searchTerm: conversationSearchTerm,
    },
    {
      query: {
        queryKey: ["conversations", conversationSearchTerm],
      },
    }
  );

  const handleGetConversationByRecipientIds = (recipientId: number): void => {
    setChatMetaData?.({
      ...chatMetaData,
      isSearchConnectionOpen: false,
    });
    const conversationAlreadyExists = conversations?.data.filter(
      (conversation) => {
        const firstRecipient = getOtherConversationUser(
          conversation.conversationRecipients
        );
        if (firstRecipient?.id === recipientId) {
          setSelectedConversationByConversationData(conversation);
          getUserMessages({ conversationId: conversation.id });
        }
        return firstRecipient?.id === recipientId;
      }
    );
    if (conversationAlreadyExists && conversationAlreadyExists?.length >= 0) {
      return;
    }

    getConversation(
      { data: { recipientIds: [recipientId] } },
      {
        onSuccess: (conversationData) => {
          const isConversationAlreadyExists = conversations?.data.some(
            (conversation) => conversation.id === conversationData.id
          );
          if (!isConversationAlreadyExists && conversations?.data) {
            setConversations({
              ...conversations,
              data: [conversationData, ...conversations.data],
            });
          }

          getUserMessages({ conversationId: conversationData.id });
          setSelectedConversationByConversationData(conversationData);
        },
        onError: () => {
          searchConnectionForChat(
            {
              data: {
                userId: recipientId,
              },
            },
            {
              onSuccess: (data) => {
                setChatMetaData?.({
                  ...chatMetaData,
                  isSearchConnectionOpen: false,
                });

                const [user] = data;
                setMessages?.(undefined);
                setSelectedConversationByUserData(user);
              },
            }
          );
        },
      }
    );
  };

  useEffect(() => {
    if (
      conversations?.data &&
      conversations?.data[0] &&
      !selectedConversation &&
      !recipientIdQueryParam
    ) {
      const firstRecipient = getOtherConversationUser(
        conversations.data[0].conversationRecipients
      );
      if (firstRecipient) {
        handleGetConversationByRecipientIds(firstRecipient.id);
        pushQueryParam({
          key: "recipient",
          value: firstRecipient?.id.toString(),
        });
      }
    }
  }, [conversations, recipientIdQueryParam]);

  useEffect(() => {
    if (conversationsData) {
      setConversations(conversationsData);
    }
  }, [conversationsData, setConversations]);

  useEffect(() => {
    if (recipientIdQueryParam) {
      handleGetConversationByRecipientIds(Number(recipientIdQueryParam));
    }
  }, [recipientIdQueryParam]);

  useEffect(() => {
    if (conversations?.data.length === 0 && !messages) {
      setChatMetaData({ ...chatMetaData, isSearchConnectionOpen: true });
    }
  }, [conversations]);

  return (
    <div className="flex flex-col items-center justify-start gap-4 w-[300px]">
      {!authData?.is_chat_blocked && (
        <Button
          onPress={() => changeSearchConnectionStatus()}
          className="rounded-xl w-full"
          startIcon={<AddPlusIcon className="w-3 h-3" />}
        >
          <Text>New Message</Text>
        </Button>
      )}
      <div className="flex flex-col justify-start  w-full border rounded-lg gap-2 pt-5 h-[500px] overflow-y-auto">
        {(conversations?.data?.length !== 0 ||
          conversationSearchTerm.length !== 0) && (
          <ConversationSearchField
            placeholder="Search by name"
            onChange={(value) => {
              setConversationSearchTerm(value);
            }}
          />
        )}
        {isLoading ? (
          <div className="flex h-[500px] justify-center items-center">
            <Spinner />
          </div>
        ) : null}
        {conversationSearchTerm.length === 0 && <NewConversationCard />}

        {conversationSearchTerm.length > 0 &&
        conversations?.data.length === 0 ? (
          <Empty text="No Conversation Found." />
        ) : null}
        {conversations?.data?.map((conversation) => (
          <ConservationItem key={conversation.id} conversation={conversation} />
        ))}
      </div>
    </div>
  );
}
